import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';

import { AUDIT_LOG_DETAIL_TYPE } from '@commons/HistoryDrawer/components/History/utils/auditLogDetails';
import Text, { ENUM_FONTS } from '@commons/Text';
import TextWithTooltip from '@commons/TextWithTooltip';

import {
  Arrow,
  DeltaDetailLine,
  DeltaSign,
  DeltaStoresContent,
  DetailLine,
  DetailsContainer,
} from './styledComponents';

const MAX_VISIBLE_STORES = 3;
const MAX_VISIBLE_STORES_DELTA = 5;

/**
 * Renders store names comma-separated, with a "+N" hover when the list exceeds the visible limit.
 *
 * @param {Array<{ id: string, name: string }>} stores
 * @param {number} maxVisible
 * @returns {React.JSX.Element | null}
 */
const renderStoreNames = (stores = [], maxVisible, inline = false) => {
  const names = stores.map(({ name }) => name).filter(Boolean);

  if (!names.length) {
    return null;
  }

  if (names.length <= maxVisible) {
    const label = names.join(', ');

    if (inline) {
      return <DeltaStoresContent>{label}</DeltaStoresContent>;
    }

    return <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_14}>{label}</Text>;
  }

  const visibleLabel = i18next.t('AUDIT_EVENTS.STORES_WITH_OVERFLOW', {
    stores: names.slice(0, maxVisible).join(', '),
    count: names.length - maxVisible,
  });

  if (inline) {
    return (
      <DeltaStoresContent>
        <TextWithTooltip content={visibleLabel} text={names.join(', ')} />
      </DeltaStoresContent>
    );
  }

  return <TextWithTooltip content={visibleLabel} text={names.join(', ')} />;
};

/**
 * Renders a store list as a single line: the first stores inline, then a "+N" trigger whose hover
 * tooltip lists the full set (the overflow pattern used on the recipe performance tab).
 *
 * @param {Array<{ id: string, name: string }>} stores
 * @returns {React.JSX.Element}
 */
const renderStoreList = (stores = []) => {
  const names = stores.map(({ name }) => name).filter(Boolean);

  if (!names.length) {
    return (
      <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_14}>
        {i18next.t('AUDIT_EVENTS.NO_ESTABLISHMENT')}
      </Text>
    );
  }

  return renderStoreNames(stores, MAX_VISIBLE_STORES);
};

const renderStoresDeltaLine = (stores, sign) => {
  if (!stores?.length) {
    return null;
  }

  return (
    <DeltaDetailLine>
      <DeltaSign>{sign}</DeltaSign>
      {renderStoreNames(stores, MAX_VISIBLE_STORES_DELTA, true)}
    </DeltaDetailLine>
  );
};

const ChangelogDetails = (props) => {
  const { details } = props;

  if (!details.length) {
    return null;
  }

  return (
    <DetailsContainer>
      {details.map((detail, index) => {
        if (detail.type === AUDIT_LOG_DETAIL_TYPE.LIST_DELTA) {
          return (
            <React.Fragment key={index}>
              {renderStoresDeltaLine(detail.itemsAdded, '+')}
              {renderStoresDeltaLine(detail.itemsRemoved, '-')}
            </React.Fragment>
          );
        }

        const label = detail.labelKey ? i18next.t(detail.labelKey) : null;
        const isStores = detail.type === AUDIT_LOG_DETAIL_TYPE.STORES;

        return (
          <DetailLine key={index}>
            {label && <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_14_WEIGHT_700_BOLD}>{label}</Text>}
            {detail.before !== undefined && (
              <>
                {isStores ? (
                  renderStoreList(detail.before)
                ) : (
                  <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_14}>{detail.before}</Text>
                )}
                <Arrow>→</Arrow>
              </>
            )}
            {isStores ? (
              renderStoreList(detail.after)
            ) : (
              <Text font={ENUM_FONTS.TEXT_SMALL_HEIGHT_14}>{detail.after}</Text>
            )}
          </DetailLine>
        );
      })}
    </DetailsContainer>
  );
};

ChangelogDetails.propTypes = {
  details: PropTypes.arrayOf(PropTypes.object),
};

ChangelogDetails.defaultProps = {
  details: [],
};

export default ChangelogDetails;
